import { Button, Form, InputGroup, Modal } from 'react-bootstrap';
import * as api from '../../api/index.js';
import useAxiosData from '../../hooks/useAxiosData.js';
import { useState } from 'react';
import TrailDialog from './TrailDialog.js';
import ColorChip from '../Form/ColorChip.js';

export default function TrailControlPanel() {
    const [trails, , setTrails] = useAxiosData(api.fetchTrails, []);
    const [showDialog, setShowDialog] = useState(false);
    /** @type {["create" | "edit", (mode: "create" | "edit") => void]} */
    const [dialogMode, setDialogMode] = useState("create");
    const [trailName, setTrailName] = useState("");
    const [editingTrail, setEditingTrail] = useState();
    const [addTrailDisabled, setAddTrailDisabled] = useState(true);

    const [trailToDelete, setTrailToDelete] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    function addTrail() {
        setEditingTrail(null);
        setDialogMode("create");
        setShowDialog(true);
    }

    function editTrail(trail) {
        setEditingTrail(trail);
        setDialogMode("edit");
        setShowDialog(true);
    }

    function confirmDeleteTrail(trail) {
        setTrailToDelete(trail);
        setShowDeleteModal(true);
    }

    async function handleDeleteTrail() {
        if (!trailToDelete) return;

        await api.deleteTrail(trailToDelete._id);
        setShowDeleteModal(false);
        setTrailToDelete(null);
        refresh();
    }

    function onNewTrailNameChange(e) {
        setTrailName(e.target.value);
        setAddTrailDisabled(!e.target.value);
    }

    function refresh() {
        api.fetchTrails().then(({ data }) => setTrails(data));
    }

    return <>
        <TrailDialog
            trailName={trailName}
            trail={editingTrail}
            mode={dialogMode}
            showDialog={showDialog}
            setShowDialog={setShowDialog}
            refresh={refresh}
        />

        {trailToDelete && (
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Delete Trail</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete the "<strong>{trailToDelete.name}</strong>" trail?
                </Modal.Body>
                <Modal.Footer style={{ display: 'flex', justifyContent: 'center', gap: '20px' }}>
                    <Button className="btn-cancel modal-action-btn" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
                    <Button variant="danger" className="modal-action-btn" onClick={handleDeleteTrail}>Delete</Button>
                </Modal.Footer>
            </Modal>
        )}

        <div>
            <h1 id="role-manager-title">Trail Manager</h1>
            <div className="container col-lg-12 col-md-10 p-3 pb-3 text-center card shadow-sm">
                <div className="col-lg-12" style={{ display: 'flex', alignItems: 'center'}}>
                    <InputGroup className="mb-3">
                        <Form.Control
                            type="text"
                            id="trailNameInput"
                            className="input-field"
                            placeholder="Trail Name"
                            value={trailName}
                            onChange={onNewTrailNameChange}
                            aria-label="Trail Name"
                            required
                            style={{ marginRight: '8px' }}
                        />
                    </InputGroup>

                    <Button 
                        id='addTrailBtn'
                        disabled={addTrailDisabled} 
                        onClick={addTrail} 
                        className="flex-shrink-0"
                        aria-label="Add new trail"
                    >
                        Add Trail
                    </Button>
                </div>

                {/* Desktop/Tablet: Table view */}
                <div className='table-responsive d-none d-md-block'>
                    <table className="table table-hover w-100">
                        <thead>
                            <tr>
                                <th>Trail</th>
                                <th>Color</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {trails.map(trail => (
                                <tr key={trail._id}>
                                    <td>{trail.name}</td>
                                    <td><ColorChip color={trail.color} /></td>
                                    <td className="d-flex gap-2 justify-content-center">
                                        <Button 
                                            onClick={() => editTrail(trail)} 
                                            className="btn-blue modal-action-btn"
                                        >
                                        Edit
                                        </Button>
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            className="modal-action-btn"
                                            onClick={() => confirmDeleteTrail(trail)}
                                        >
                                            <i className="fas fa-trash-alt me-1"></i>
                                            Delete
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Mobile: Card view */}
                <div className='d-md-none'>
                    {trails.length > 0 ? (
                        trails.map(trail => (
                            <div key={trail._id} className="card mb-3 shadow-sm">
                                <div className="card-body">
                                    <h2 className="card-title mb-3 h5">{trail.name}</h2>
                                    
                                    <div className="d-flex align-items-center justify-content-center gap-2 mb-3 pb-3" style={{ borderBottom: '1px solid #dee2e6' }}>
                                        <span className="fw-bold">Trail Color:</span>
                                        <ColorChip color={trail.color} />
                                    </div>
                                    
                                    <div className="d-grid gap-2">
                                        <Button 
                                            id='editTrailBtn'
                                            onClick={() => editTrail(trail)} 
                                            className="btn-blue w-100"
                                            size="sm"
                                        >
                                            <i className="fas fa-edit me-2"></i>
                                            Edit Trail
                                        </Button>
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            className="w-100"
                                            onClick={() => confirmDeleteTrail(trail)}
                                        >
                                            <i className="fas fa-trash-alt me-2"></i>
                                            Delete Trail
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center p-4 text-muted">
                            No trails found
                        </div>
                    )}
                </div>
            </div>
        </div>
    </>;
}
